import {useEffect, useMemo, useRef, useState} from 'react';
import type {PartialDeep} from 'type-fest';
import Uppy, {type UppyOptions} from '@uppy/core';
import type {ThumbnailOptions} from '@uppy/thumbnail-generator';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {UppyMultiDestinationOptions} from '../lib/multi-destination';
import {UppyStateStore} from '../store';
import type {AnyUppyFile, UppyFileMeta} from '../types';
import {
	getUppyFileIdFromFile,
	getUppyFileObjectFromFile,
	createUppyMultiDestinationInstance,
	createUppyMultiDestinationOptions,
} from '../utils';

export type UseUploadParams<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = PartialDeep<UppyMultiDestinationOptions> & {
	/**
	 * files to (pre)add\
	 * NOTE: whenever this changes the files will be added to the `uppy` instance\
	 * it is recommended to memoize this from the outside
	 */
	files?: File[];
	/**
	 * custom uppy state store instance\
	 * NOTE: will have no effect if a custom `uppy` instance was passed
	 */
	store?: TStore;
	/**
	 * options passed to the thumbnail generator plugin\
	 * NOTE: will have no effect if `useThumbnail` is `false`
	 */
	thumnailOptions?: Partial<ThumbnailOptions>;
	/**
	 * options passed to uppy
	 * NOTE: will have no effect if a custom `uppy` instance was passed
	 */
	uppyOptions?: Partial<Omit<UppyOptions<TMeta>, 'store'>>;
	/** custom uppy instance */
	uppy?: Uppy<TStore>;
	/**
	 * use the thumbnail generator plugin\
	 * NOTE: will have no effect if a custom `uppy` instance was passed
	 */
	useThumbnail?: boolean;
	/** will be called whenever `addFiles` encounters a file that was missing from a recovered state */
	onFilesReplace?: (files: File[]) => void;
};

/**
 * React hook wrapper for uppy\
 * Adds some helpers for managing an uppy instance and allows for passing a pre-existing uppy instance or uppy state store
 * @param params UseUploadParams
 * @returns
 */
export const useUpload = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>({
	files,
	uppy: _uppy,
	store,
	thumnailOptions,
	uppyOptions,
	useThumbnail,
	onFilesReplace,
	...multiDestinationUploadOptions
}: UseUploadParams<TMeta, TStore> = {}) => {
	const uppyUncontrolled = useRef(_uppy == null);
	const storeUncontrolled = useRef(store == null);

	// if an instance of `uppy` was provided, use that instance's store
	// if a store instance was provided, use that instance
	// otherwise create a new store
	const uppyStoreRef = useRef(
		_uppy?.store ?? store ?? new UppyStateStore<TMeta>(),
	);

	const [uppyInstance, setUppyInstance] = useState<Uppy<TStore>>(
		() =>
			_uppy ||
			createUppyMultiDestinationInstance<TMeta, TStore>({
				multiDestinationOptions: createUppyMultiDestinationOptions(
					multiDestinationUploadOptions,
				),
				store: uppyStoreRef.current,
				thumnailOptions,
				uppyOptions,
				useThumbnail,
			}),
	);

	// when these things change we need to recreate the uppy instance
	// only necessary when a custom `uppy` instance was not provided
	useWhenChangedEffect(
		[
			uppyOptions,
			multiDestinationUploadOptions,
			useThumbnail,
			thumnailOptions,
		] as const,
		(
			[
				uppyOptions,
				multiDestinationUploadOptions,
				useThumbnail,
				thumnailOptions,
			],
			oldValues,
		) => {
			// we are not managing the uppy instance
			if (!uppyUncontrolled.current) return;

			cleanupUppy();
			// if we are handling the store, recreate
			if (storeUncontrolled.current)
				uppyStoreRef.current = new UppyStateStore<TMeta>();

			setUppyInstance(
				createUppyMultiDestinationInstance<TMeta, TStore>({
					multiDestinationOptions: createUppyMultiDestinationOptions(
						multiDestinationUploadOptions as UppyMultiDestinationOptions,
					),
					store: uppyStoreRef.current,
					thumnailOptions,
					uppyOptions,
					useThumbnail,
				}),
			);
		},
	);

	// reflect the files passed in from the outside
	useEffect(() => {
		if (!files) return;
		addFiles(files);
	}, [files]);

	const cleanupUppy = useConstCallback(() => {
		uppyInstance.close();
		// if we are handling the store, clean that up too
		if (storeUncontrolled.current) uppyStoreRef.current.dispose();
	});

	// cleanup on unmount
	useEffect(() => {
		return () => {
			if (!uppyUncontrolled.current) return;
			cleanupUppy();
		};
	}, []);

	// uppy does not offer a great way to get files in when not using their whole dashboard

	/** add files to uppy */
	const addFiles = useConstCallback(
		(files: File | File[], meta?: Partial<TMeta>) => {
			const uppyFiles = uppyInstance.getState<TMeta>().files;

			files = Array.isArray(files) ? files : [files];

			// check if any files belong to records with missing data (recovered but no reference to the original file)
			if (onFilesReplace) {
				const filesReplaced: File[] = [];
				files.forEach((file) => {
					const fileId = getUppyFileIdFromFile(file);
					const uppyFile = uppyFiles[fileId];
					if (uppyFile && uppyFile.isGhost) {
						filesReplaced.push(file);
						return;
					}
				});
				onFilesReplace(filesReplaced);
			}

			uppyInstance.addFiles(
				files.map((file) => getUppyFileObjectFromFile(file, meta)),
			);
		},
	);

	/** remove files from uppy */
	const removeFiles = useConstCallback((fileIds: string | string[]) =>
		uppyInstance.removeFiles(
			Array.isArray(fileIds) ? fileIds : [fileIds],
			'removed-by-user',
		),
	);

	/** remove all pending (non submitted) files from uppy */
	const removeAllPendingFiles = useConstCallback(() => {
		removeFiles(uppyStoreRef.current.pendingFiles.map((file) => file.id));
	});

	/** remove all files from uppy */
	const removeAllFiles = useConstCallback(() => {
		removeFiles(Object.keys(uppyInstance.getState<TMeta>().files) as string[]);
	});

	/**
	 * tell thumbnail generator to generate thumbnail for file\
	 * NOTE: this will have no effect if `thumbnailOptions.lazy` is `false`\
	 */
	const requestFilePreview = useConstCallback((file?: AnyUppyFile) => {
		if (!file) return;
		uppyInstance.emit('thumbnail:request', file);
	});

	/** tell thumbnail generator to cancel (or throw away) generation of thumbnail for file */
	const cancelFilePreview = useConstCallback((file?: AnyUppyFile) => {
		if (!file) return;
		uppyInstance.emit('thumbnail:cancel', file);
	});

	return {
		uppy: uppyInstance,
		uppyStore: uppyStoreRef.current,
		addFiles,
		removeFiles,
		removeAllFiles,
		removeAllPendingFiles,
		requestFilePreview,
		cancelFilePreview,
	};
};

export default useUpload;

// Typescript cannot infer `ReturnType` of a generic, so you must roll your own
class InferWrapper<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> {
	mediate = (...args: any[]) => useUpload<TMeta, TStore>(...args);
}

export type UseUploadType<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = ReturnType<InferWrapper<TMeta, TStore>['mediate']>;
